// @ts-nocheck
import { useForm, useFieldArray } from "react-hook-form"
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup"
import TextEditor from '../../../../components/TextEditor';
import TextField from '../../../../components/TextField';
import Checkbox from '../../../../components/CheckBox';
import Button from '../../../../components/Button';
import ModalPopup from '../../../../components/Modal';
import { Card, notification } from 'antd';
import deleteIcon from '../../../../assets/icons/deleteIcon.svg';
import closeIcon from '../../../../assets/icons/adminScreen/closeIcon.svg';
import { useEvent } from '../../../../hooks/useEvent';

// Create validation schema
const courseItemSchema = yup.object({
  title: yup.string().required("Title is required"),
  url: yup.string().required("URL is required").url("Must be a valid URL"),
});

const courseSectionSchema = yup.object({
  title: yup.string().required("Section title is required"),
  items: yup.array().of(courseItemSchema).min(1, "Add at least one item"),
});

const schema = yup.object({
  courseTitle: yup.string().required("Course title is required"),
  courseDescription: yup.string().required("Course description is required"),
  otherDetails: yup.string().optional(),
  content: yup.array().of(courseSectionSchema).min(1, "Add at least one section"),
});

// Separate component for course section to properly handle hooks
function CourseSection({
  sectionIndex,
  control,
  register,
  errors,
  contentFields,
  onRemoveSection,
}) {
  // Field array for items within this section
  const {
    fields: itemFields,
    append: appendItem,
    remove: removeItem,
  } = useFieldArray({
    control,
    name: `content[${sectionIndex}].items`,
  })

  return (
    <div className="space-y-4 p-4 border rounded-md">
      <div className="flex items-center justify-between">
        <div className="text-base font-semibold font-['Manrope']">Section Title</div>
        {contentFields?.length > 1 && (
          <div
            role="none"
            className="w-8 h-8 relative cursor-pointer"
            onClick={() => onRemoveSection()}
          >
            <img src={deleteIcon} alt="icon" />
          </div>
        )}
      </div>
      <div className="flex-1 space-y-4">
        <div>
          <TextField
            name={`content[${sectionIndex}].title`}
            type="text"
            label="Section Title"
            control={control}
            errors={errors}
            customHeight="40px"
            placeholder="Enter section title"
            customlabelclass="text-zinc-700 !text-sm !font-normal font-['Manrope'] !pb-2"
          />
        </div>
      </div>

      <Card>
        <div className="flex items-center justify-between pb-2">
          <div className="text-sm font-semibold font-['Manrope']">Section Items</div>
          <Button
            type="button"
            variant="primary-outline"
            onClick={() => appendItem({ title: "", url: "" })}
          >
            <div className="text-indigo-600 text-base font-bold font-['Manrope']">
              Add Item
            </div>
          </Button>
        </div>

        <div className="space-y-4">
          {itemFields?.map((itemField, itemIndex) => (
            <div key={itemField.id} className="grid grid-cols-12 gap-4 p-3 border rounded-md">
              <div className="col-span-5 space-y-2">
                <TextField
                  name={`content[${sectionIndex}].items[${itemIndex}].title`}
                  type="text"
                  label="Title"
                  control={control}
                  errors={errors}
                  customHeight="40px"
                  placeholder="Enter item title"
                  customlabelclass="text-zinc-700 !text-sm !font-normal font-['Manrope'] !pb-2"
                />
              </div>
              <div className="col-span-6 space-y-2">
                <TextField
                  name={`content[${sectionIndex}].items[${itemIndex}].url`}
                  type="text"
                  label="Youtube Url"
                  control={control}
                  errors={errors}
                  customHeight="40px"
                  placeholder="Enter Youtube url"
                  customlabelclass="text-zinc-700 !text-sm !font-normal font-['Manrope'] !pb-2"
                />
              </div>
              <div className="col-span-1 flex items-end justify-end">
                {itemFields.length > 1 && (
                  <div
                    role="none"
                    className="w-8 h-8 relative cursor-pointer"
                    onClick={() => removeItem(itemIndex)}
                  >
                    <img src={deleteIcon} alt="icon" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}

export default function EventContentForm({
  eventContentForm,
  setEventContentForm,
  refreshEvents,
}) {
  const { createEventContent, editEventContent } = useEvent();
  const { eventContentFormData: { courseContent }  } = eventContentForm;
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: eventContentForm?.eventContentFormData?.courseContent ?? {
      courseTitle: "",
      courseDescription: "",
      otherDetails: "",
      isCourseContentPublished: false,
      content: [{ title: "", items: [{ title: "", url: "" }] }],
    },
  })

  // Field array for sections
  const {
    fields: contentFields,
    append: appendContent,
    remove: removeContent,
  } = useFieldArray({
    control,
    name: "content",
  })

  const onSubmit = async (data) => {
    try {
      const request = {
        eventId: eventContentForm?.eventContentFormData._id,
        courseContent: { 
          courseTitle: data?.courseTitle,
          courseDescription: data?.courseDescription,
          otherDetails: data?.otherDetails,
          content: data?.content,
          isCourseContentPublished: data?.isCourseContentPublished || false,
         },
      };

      const response = await createEventContent(request);
      notification.success({
        message: response?.message || `Successfully ${courseContent ? 'updated' : 'created'} event content`,
        duration: 3,
      });
      if (refreshEvents) {
        refreshEvents();
      }
      setEventContentForm({ eventContentForm: null, openEventContentPopup: false });
    } catch (error) {
      notification.error({
        message: error?.message ?? `Failed to ${courseContent ? 'edit' : 'create'} event content`,
        duration: 3,
      });
    }
  };

  return (
    <ModalPopup fullScreen>
      <div
        className="w-full h-full pt-8 pb-12 bg-purple-50 flex-col justify-start
          items-start gap-8 inline-flex overflow-auto"
      >
        <div className="self-stretch px-12 justify-between items-center inline-flex">
          <div
            className="text-indigo-600 text-xl font-semibold font-['Manrope']"
          >
            {courseContent ? 'Edit Event\'s Content Library' : 'New Event\'s Content Library'}
          </div>
          <div
            role="none"
            className="w-8 h-8 relative cursor-pointer"
            onClick={() => setEventContentForm({ eventContentForm: null, openEventContentPopup: false })}
          >
            <img src={closeIcon} alt="icon" />
          </div>
        </div>
        <div className="self-stretch h-full px-12 flex-col justify-start items-start gap-[26px] flex">
          <div
            className="self-stretch h-full p-6 bg-white rounded-lg flex-col justify-center items-start gap-4 flex"
          >
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8 w-full">
              <div className="space-y-2 w-full">
                <TextField
                  name="courseTitle"
                  type="text"
                  label="Course Title"
                  control={control}
                  errors={errors}
                  customHeight="40px"
                  placeholder="Enter course title"
                  customlabelclass="text-zinc-700 !text-sm !font-normal font-['Manrope'] !pb-2"
                />

                <div className="space-y-2">
                  <div className="text-zinc-700 text-sm text-left font-normal font-['Manrope']">
                    Course Description
                  </div>
                  <TextEditor
                    name="courseDescription"
                    label="Course Description"
                    control={control}
                    errors={errors}
                    placeholder="Enter course description"
                  />
                </div>

                <div className="space-y-2">
                  <div className="text-zinc-700 text-sm text-left font-normal font-['Manrope']">
                    Other Details
                  </div>
                  <TextEditor
                    control={control}
                    errors={errors}
                    label="Other Details"
                    name="otherDetails"
                    placeholder="Enter other details"

                  />
                </div>
              </div>

              <div className="flex items-center justify-between pb-2">
                <div className="text-zinc-700 text-base text-left font-semibold font-['Manrope']">
                  Content Library
                </div>
                <Button
                  type="button"
                  variant="primary-outline"
                  onClick={() => appendContent({ title: "", items: [{ title: "", url: "" }] })}
                >
                  <div className="text-indigo-600 text-base font-bold font-['Manrope']">
                    Add Section
                  </div>
                </Button>
              </div>
              <div className="space-y-4">
                {contentFields?.map((field, sectionIndex) => (
                  <CourseSection
                    key={field.id}
                    sectionIndex={sectionIndex}
                    control={control}
                    register={register}
                    errors={errors}
                    contentFields={contentFields}
                    onRemoveSection={() => removeContent(sectionIndex)}
                  />
                ))}
              </div>

              <div className="col-span-12 py-2">
                <Checkbox
                  name="isCourseContentPublished"
                  label="Publish Event's Content Library"
                  labelFirst
                  control={control}
                  errors={errors}
                  customlabelclass="text-zinc-700 !text-sm !font-normal font-['Manrope']"
                />
              </div>
              <div className="flex justify-start">
                <Button
                  type="submit"
                >
                  <div className="text-white text-base font-bold font-['Manrope']">
                    Save Content Library
                  </div>
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </ModalPopup>
  )
}
